import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase'
import { useAuth } from '@/contexts/AuthContext'
import * as XLSX from 'xlsx'
import { BarChart2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { PitStatsTable } from './PitStatsTable'
import { DerniersContacts } from './DerniersContacts'
import { displayBeneficiaryName } from '@/utils/beneficiaryDisplay'
import { fetchAllPages, PAGE_SIZE } from '@/lib/fetchAllPages'
import { buildNoteRows, EXPORTED_NOTE_COLUMNS, exportFileName, type ExportedNote } from '@/lib/notesExport'

interface AnalyticsData {
    totalExchanges: number
    totalBeneficiaries: number
    totalWorkers: number
    averageDuration: number
    monthlyExchanges: Array<{ month: string; count: number }>
    topWorkers: Array<{ name: string; exchanges: number }>
    exchangeFormats: Array<{ format: string; count: number }>
    scoreDistribution: Array<{ category: string; avg_score: number }>
    rdrDistribution: Array<{ value: string; count: number }>
}

interface FilteredData {
    selectedBeneficiaryIds: string[]
    selectedWorkerIds: string[]
}

const downloadWorkbook = (
    rows: Record<string, unknown>[],
    sheetName: string,
    fileName: string,
    columnWidths: Record<string, number> = {},
) => {
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(rows)
    worksheet['!cols'] = Object.keys(rows[0] || {}).map(key => ({
        wch: columnWidths[key] ?? Math.min(Math.max(key.length, 10), 50)
    }))
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
    XLSX.writeFile(workbook, fileName)
}

export const DataAnalysis = ({ onNavigate, onNavigateToBeneficiaire }: { onNavigate: (route: string) => void; onNavigateToBeneficiaire?: (id: string) => void }) => {
    const { userProfile } = useAuth()
    const [activeTab, setActiveTab] = useState<'pit' | 'contacts' | 'other'>('contacts')
    const [showConfirm, setShowConfirm] = useState(false)
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
    const [filteredData, setFilteredData] = useState<FilteredData>({
        selectedBeneficiaryIds: [],
        selectedWorkerIds: []
    })
    const [beneficiaries, setBeneficiaries] = useState<Array<{ id: string; name: string; isActive: boolean }>>([])
    const [workers, setWorkers] = useState<Array<{ id: string; name: string }>>([])
    const [loading, setLoading] = useState(true)
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 months ago
        endDate: new Date().toISOString().split('T')[0] // today
    })
    const [totalRecords, setTotalRecords] = useState(0)
    const [isLoadingBatch, setIsLoadingBatch] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [isExportingNotes, setIsExportingNotes] = useState(false)

    useEffect(() => {
        loadFilters()
    }, [])

    // Manual refresh function for when user is happy with filters
    const handleRefreshData = () => {
        if (filteredData.selectedBeneficiaryIds.length > 0 || filteredData.selectedWorkerIds.length > 0) {
            loadBasicAnalytics()
        }
    }

    // Shared by the stats and both exports. With every worker selected no worker filter is sent,
    // so rows without an author are kept.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const applyFilters = (query: any, dateColumn: string, filters: FilteredData, workerCount: number) => {
        let filtered = query
            .gte(dateColumn, dateRange.startDate)
            .lte(dateColumn, dateRange.endDate)
            .not(dateColumn, 'is', null)
        if (filters.selectedWorkerIds.length > 0 && filters.selectedWorkerIds.length < workerCount) {
            filtered = filtered.in('worker_id', filters.selectedWorkerIds)
        }
        if (filters.selectedBeneficiaryIds.length > 0) {
            filtered = filtered.in('beneficiary_id', filters.selectedBeneficiaryIds)
        }
        return filtered
    }

    const fetchFilteredExchanges = (filters: FilteredData, workerCount: number) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fetchAllPages<any>((from, to) =>
            applyFilters(supabase.from('stats').select('*'), 'stat_date', filters, workerCount)
                .order('stat_date', { ascending: false })
                .order('id')
                .range(from, to)
        )

    const fetchFilteredNotes = (filters: FilteredData, workerCount: number) =>
        fetchAllPages<ExportedNote>((from, to) =>
            applyFilters(supabase.from('notes').select(EXPORTED_NOTE_COLUMNS), 'note_date', filters, workerCount)
                .is('deleted_dt', null)
                .order('note_date', { ascending: false })
                .order('id')
                .range(from, to)
        )

    // Names as shown in the filter lists, so both export files match what the app displays.
    const nameMaps = () => ({
        workerNames: new Map(workers.map(w => [w.id, w.name])),
        tenantNames: new Map(beneficiaries.map(b => [b.id, b.name])),
    })

    // workerCount is passed on the first load, before the workers state is set.
    const loadBasicAnalytics = async (customFilters?: FilteredData, workerCount = workers.length) => {
        try {
            setLoading(true)

            const filters = customFilters || filteredData

            const { count } = await applyFilters(
                supabase.from('stats').select('*', { count: 'exact', head: true }),
                'stat_date',
                filters,
                workerCount,
            )

            setTotalRecords(count || 0)
            setIsLoadingBatch((count || 0) > PAGE_SIZE)

            const exchanges = await fetchFilteredExchanges(filters, workerCount)

            if (!exchanges.length) {
                setAnalyticsData({
                    totalExchanges: 0,
                    totalBeneficiaries: 0,
                    totalWorkers: 0,
                    averageDuration: 0,
                    monthlyExchanges: [],
                    topWorkers: [],
                    exchangeFormats: [],
                    scoreDistribution: [],
                    rdrDistribution: []
                })
                return
            }

            // Deduplicate: group by (beneficiary_id, stat_date), average scores within group
            const scoreCategories = [
                'postprocess_cvc_comportement',
                'postprocess_cvc_corps',
                'postprocess_cvc_vetements',
                'postprocess_hestia_bailleur',
                'postprocess_hestia_hygiene',
                'postprocess_hestia_voisinage',
                'postprocess_hestia2_securite',
                'postprocess_hestia2_sante',
                'postprocess_hestia2_consommation'
            ]

            const groups = new Map<string, any[]>()
            for (const e of exchanges) {
                const key = `${e.beneficiary_id}|${e.stat_date}`
                if (!groups.has(key)) groups.set(key, [])
                groups.get(key)!.push(e)
            }

            const deduped = Array.from(groups.values()).map(group => {
                if (group.length === 1) return group[0]
                const base = { ...group[0] }
                for (const field of scoreCategories) {
                    const vals = group.map(e => e[field]).filter((v: any) => v !== null && v !== undefined)
                    base[field] = vals.length > 0 ? vals.reduce((s: number, v: number) => s + v, 0) / vals.length : null
                }
                // average duration too
                const durations = group.map(e => e.duree).filter((d: any) => d !== null && d !== undefined)
                base.duree = durations.length > 0 ? durations.reduce((s: number, d: number) => s + d, 0) / durations.length : null
                return base
            })

            // Calculate monthly exchanges (deduplicated)
            const monthlyData = deduped.reduce((acc: Record<string, number>, e) => {
                const month = new Date(e.stat_date).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'short'
                })
                acc[month] = (acc[month] || 0) + 1
                return acc
            }, {})

            const monthlyExchanges = Object.entries(monthlyData)
                .map(([month, count]) => ({ month, count }))
                .slice(0, 6)

            // Get exchange formats distribution (deduplicated)
            const formatData = deduped.reduce((acc: Record<string, number>, e) => {
                const format = e.format_echange || 'Non spécifié'
                acc[format] = (acc[format] || 0) + 1
                return acc
            }, {})

            const exchangeFormats = Object.entries(formatData)
                .map(([format, count]) => ({ format, count }))

            // Score averages: already averaged within group, now average across deduped records
            const scoreDistribution = scoreCategories.map(category => {
                const scores = deduped
                    .map(e => e[category as keyof typeof e] as number)
                    .filter(score => score !== null && score !== undefined)

                const avgScore = scores.length > 0
                    ? scores.reduce((sum, score) => sum + score, 0) / scores.length
                    : 0

                return {
                    category: category.replace('postprocess_', '').replace('_', ' '),
                    avg_score: Math.round(avgScore * 100) / 100
                }
            })

            // Unique counts (deduplicated)
            const uniqueBeneficiaries = new Set(deduped.map(e => e.beneficiary_id).filter(Boolean)).size
            const uniqueWorkers = new Set(exchanges.map(e => e.worker_id).filter(Boolean)).size

            const totalDuration = deduped
                .map(e => e.duree)
                .filter(d => d !== null && d !== undefined)
                .reduce((sum: number, duration: number) => sum + duration, 0)

            const validDurations = deduped.filter(e => e.duree !== null && e.duree !== undefined).length

            const rdrData = exchanges.reduce((acc: Record<string, number>, exchange) => {
                const val = exchange.distribution_kit_rdr
                if (val && val !== 'Non') acc[val] = (acc[val] || 0) + 1
                return acc
            }, {})
            const rdrDistribution = Object.entries(rdrData)
                .map(([value, count]) => ({ value, count }))
                .sort((a, b) => b.count - a.count)

            setAnalyticsData({
                totalExchanges: deduped.length,
                totalBeneficiaries: uniqueBeneficiaries,
                totalWorkers: uniqueWorkers,
                averageDuration: validDurations > 0 ? Math.round((totalDuration / validDurations) * 100) / 100 : 0,
                monthlyExchanges,
                topWorkers: [],
                exchangeFormats,
                scoreDistribution,
                rdrDistribution
            })

        } catch (error) {
            console.error('Error loading analytics:', error)
        } finally {
            setIsLoadingBatch(false)
            setLoading(false)
        }
    }

    const loadFilters = async () => {
        try {
            const [beneficiariesResult, workersResult] = await Promise.all([
                // Former tenants too: their exchanges and notes belong in historical analyses
                supabase.from('beneficiaries').select('id, prenom, nom, numero_suivi, is_active').order('nom'),
                // Fetch both active and inactive workers for data analysis
                supabase.from('workers').select('id, name, is_active').order('name')
            ])

            const beneficiariesData = (beneficiariesResult.data || [])
                .filter(b => b.prenom || b.nom)
                .map(b => ({ id: b.id, name: displayBeneficiaryName(b), isActive: b.is_active !== false }))
            const workersData = (workersResult.data || [])
                .filter(w => w.name) // Filter out null names  
                .map(w => ({ id: w.id, name: w.name || 'Inconnu' })) // Provide default name

            setBeneficiaries(beneficiariesData)
            setWorkers(workersData)

            // Set all as selected by default
            const newFilterData = {
                selectedBeneficiaryIds: beneficiariesData.map(b => b.id),
                selectedWorkerIds: workersData.map(w => w.id)
            }

            setFilteredData(newFilterData)

            // Load initial analytics data with all items selected
            if (beneficiariesData.length > 0 || workersData.length > 0) {
                // Call loadBasicAnalytics with the new filter data
                loadBasicAnalytics(newFilterData, workersData.length)
            }
        } catch (error) {
            console.error('Error loading filters:', error)
            setLoading(false)
        }
    }



    const exportToExcelInternal = async (customFilters?: FilteredData) => {
        try {
            setIsExporting(true)

            const exchanges = await fetchFilteredExchanges(customFilters || filteredData, workers.length)

            if (exchanges.length === 0) {
                alert('Aucune donnée à exporter pour cette période')
                return
            }

            const { workerNames: workerMap, tenantNames: beneficiaryMap } = nameMaps()

            // Transform data for Excel export
            const excelData = exchanges.map(exchange => ({
                // Basic Info
                'ID': exchange.id,
                'Date': exchange.stat_date,
                'Travailleur': workerMap.get(exchange.worker_id || '') || 'Non assigné',
                // Colleagues after worker
                'Collègues': exchange.colleague_ids
                    ? exchange.colleague_ids
                        .map((id: string) => workerMap.get(id) || `ID inconnu: ${id}`)
                        .join(', ')
                    : '',
                'Locataire': beneficiaryMap.get(exchange.beneficiary_id || '') || 'Non assigné',
                'Durée (min)': exchange.duree,
                'Format échange': exchange.format_echange,
                'Présence': exchange.presence,
                'Entrée appartement': exchange.entree_dans_appartement,
                'Contenu échange': exchange.contenu_echange,
                'Contenu réel': exchange.contenu_echange_real,
                'Note': exchange.note,
                'Commentaire': exchange.comment,
                'Distribution kit RDR': exchange.distribution_kit_rdr,
                'Timestamp soumission': exchange.submit_ts,

                // CVC Scores (original)
                'CVC Comportement': exchange.cvc_comportement,
                'CVC Corps': exchange.cvc_corps,
                'CVC Vêtements': exchange.cvc_vetements,

                // Hestia Scores (original)
                'Hestia Voisinage': exchange.hestia_voisinage,
                'Hestia Hygiène': exchange.hestia_hygiene,
                'Hestia Bailleur': exchange.hestia_bailleur,

                // Hestia 2 Scores (original)
                'Hestia2 Sécurité': exchange.hestia2_securite,
                'Hestia2 Santé': exchange.hestia2_sante,
                'Hestia2 Consommation': exchange.hestia2_consommation,

                // Post-processed Scores (numerical)
                'Score CVC Comportement': exchange.postprocess_cvc_comportement,
                'Score CVC Corps': exchange.postprocess_cvc_corps,
                'Score CVC Vêtements': exchange.postprocess_cvc_vetements,
                'Score Hestia Voisinage': exchange.postprocess_hestia_voisinage,
                'Score Hestia Hygiène': exchange.postprocess_hestia_hygiene,
                'Score Hestia Bailleur': exchange.postprocess_hestia_bailleur,
                'Score Hestia2 Sécurité': exchange.postprocess_hestia2_securite,
                'Score Hestia2 Santé': exchange.postprocess_hestia2_sante,
                'Score Hestia2 Consommation': exchange.postprocess_hestia2_consommation,

                // Meta fields
                'Est CVC': exchange.postprocess_is_cvc ? 'Oui' : 'Non',
                'Est Hestia': exchange.postprocess_is_hestia ? 'Oui' : 'Non',
                'CSV Hestia rempli': exchange.postprocess_is_csv_hestia_filled ? 'Oui' : 'Non',
                'Nombre travailleurs': exchange.postprocess_n_travailleurs,
                'Créé par': exchange.created_by,
                'Créé le': exchange.created_at,
                'Modifié le': exchange.updated_at
            }))

            downloadWorkbook(excelData, 'Échanges', exportFileName('Echanges', dateRange.startDate, dateRange.endDate))

            console.log(`Export Excel terminé: ${exchanges.length} échanges exportés`)

        } catch (error) {
            console.error('Error exporting to Excel:', error)
            alert('Erreur lors de l\'export Excel')
        } finally {
            setIsExporting(false)
        }
    }

    // Same filters as the exchanges export, in a file of its own
    const exportNotes = async () => {
        try {
            setIsExportingNotes(true)

            const notes = await fetchFilteredNotes(filteredData, workers.length)

            if (notes.length === 0) {
                alert('Aucune note à exporter pour cette période')
                return
            }

            const { workerNames, tenantNames } = nameMaps()
            downloadWorkbook(
                buildNoteRows(notes, workerNames, tenantNames),
                'Notes',
                exportFileName('Notes', dateRange.startDate, dateRange.endDate),
                { 'Note': 80, 'Commentaire': 40 },
            )

            console.log(`Export notes terminé: ${notes.length} notes exportées`)
        } catch (error) {
            console.error('Error exporting notes:', error)
            alert('Erreur lors de l\'export des notes')
        } finally {
            setIsExportingNotes(false)
        }
    }

    // Wrapper function for onClick handler
    const exportToExcel = () => {
        exportToExcelInternal()
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">
                        {isLoadingBatch
                            ? `Chargement en cours... (${totalRecords} enregistrements trouvés)`
                            : 'Chargement des données d\'analyse...'
                        }
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Analyse de données" icon={<BarChart2 size={20} />} onBack={() => onNavigate('dashboard')} />

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8">
                <div className="flex gap-0">
                    {([['pit', 'Analyse PIT'], ['contacts', 'Derniers contacts'], ['other', 'Autres stats']] as const).map(([tab, label]) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab
                                    ? 'border-blue-600 text-blue-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {activeTab === 'pit' && (
                <main className="py-4 px-4">
                    <PitStatsTable />
                </main>
            )}

            {activeTab === 'contacts' && (
                <DerniersContacts
                    embedded
                    onNavigateHome={() => onNavigate('home')}
                    showConfirm={showConfirm}
                    onConfirmNavigateHome={() => { setShowConfirm(false); onNavigate('home') }}
                    onCancelConfirm={() => setShowConfirm(false)}
                    onNavigateToBeneficiaire={onNavigateToBeneficiaire}
                />
            )}

            {activeTab === 'other' && (
            <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                {/* Filter Section */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>

                    {/* Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date de début
                            </label>
                            <input
                                type="date"
                                value={dateRange.startDate}
                                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                className="w-full p-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date de fin
                            </label>
                            <input
                                type="date"
                                value={dateRange.endDate}
                                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                className="w-full p-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="text-sm text-gray-600">
                            <div className="bg-blue-50 p-3 rounded-md">
                                <div className="flex justify-between items-center gap-2 mb-2">
                                    <p><strong>Enregistrements filtrés:</strong> {totalRecords.toLocaleString()}</p>
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={exportToExcel}
                                            disabled={isExporting || !analyticsData || totalRecords === 0}
                                            size="sm"
                                            className="bg-green-600 hover:bg-green-700 text-xs"
                                        >
                                            {isExporting ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                    Export...
                                                </>
                                            ) : (
                                                <>
                                                    📊 Excel
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            onClick={exportNotes}
                                            disabled={isExportingNotes || workers.length === 0}
                                            size="sm"
                                            className="bg-green-600 hover:bg-green-700 text-xs"
                                            title="Notes des mêmes dates, travailleurs et locataires, dans un fichier séparé"
                                        >
                                            {isExportingNotes ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                    Export...
                                                </>
                                            ) : (
                                                <>
                                                    📝 Notes
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                <p><strong>Période:</strong> {Math.ceil((new Date(dateRange.endDate).getTime() - new Date(dateRange.startDate).getTime()) / (1000 * 60 * 60 * 24))} jours</p>
                                {totalRecords > 1000 && (
                                    <p className="text-orange-600 mt-1">
                                        ⚡ Chargement par lots activé
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Multi-select Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Travailleurs ({filteredData.selectedWorkerIds.length}/{workers.length} sélectionnés)
                            </label>
                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                                <div className="flex items-center mb-2">
                                    <input
                                        type="checkbox"
                                        checked={filteredData.selectedWorkerIds.length === workers.length}
                                        onChange={(e) => {
                                            const newSelection = e.target.checked ? workers.map(w => w.id) : []
                                            setFilteredData(prev => ({ ...prev, selectedWorkerIds: newSelection }))
                                        }}
                                        className="mr-2"
                                    />
                                    <span className="font-medium">Sélectionner tout</span>
                                </div>
                                {workers.map(worker => (
                                    <div key={worker.id} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={filteredData.selectedWorkerIds.includes(worker.id)}
                                            onChange={(e) => {
                                                const newSelection = e.target.checked
                                                    ? [...filteredData.selectedWorkerIds, worker.id]
                                                    : filteredData.selectedWorkerIds.filter(id => id !== worker.id)
                                                setFilteredData(prev => ({ ...prev, selectedWorkerIds: newSelection }))
                                            }}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">{worker.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Locataires ({filteredData.selectedBeneficiaryIds.length}/{beneficiaries.length} sélectionnés)
                            </label>
                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                                <div className="flex items-center mb-2">
                                    <input
                                        type="checkbox"
                                        checked={filteredData.selectedBeneficiaryIds.length === beneficiaries.length}
                                        onChange={(e) => {
                                            const newSelection = e.target.checked ? beneficiaries.map(b => b.id) : []
                                            setFilteredData(prev => ({ ...prev, selectedBeneficiaryIds: newSelection }))
                                        }}
                                        className="mr-2"
                                    />
                                    <span className="font-medium">Sélectionner tout</span>
                                </div>
                                {beneficiaries.map(beneficiary => (
                                    <div key={beneficiary.id} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={filteredData.selectedBeneficiaryIds.includes(beneficiary.id)}
                                            onChange={(e) => {
                                                const newSelection = e.target.checked
                                                    ? [...filteredData.selectedBeneficiaryIds, beneficiary.id]
                                                    : filteredData.selectedBeneficiaryIds.filter(id => id !== beneficiary.id)
                                                setFilteredData(prev => ({ ...prev, selectedBeneficiaryIds: newSelection }))
                                            }}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">
                                            {beneficiary.name}
                                            {!beneficiary.isActive && <span className="text-gray-400"> (inactif)</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Manual Refresh Button */}
                    <div className="mt-6 flex justify-center">
                        <Button
                            onClick={handleRefreshData}
                            disabled={filteredData.selectedBeneficiaryIds.length === 0 && filteredData.selectedWorkerIds.length === 0}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Chargement...
                                </>
                            ) : (
                                <>
                                    🔄 Actualiser les données
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {analyticsData && (
                    <>
                        {/* Overview Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Échanges</h3>
                                <p className="text-3xl font-bold text-blue-600">{analyticsData.totalExchanges}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Locataires</h3>
                                <p className="text-3xl font-bold text-green-600">{analyticsData.totalBeneficiaries}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Travailleurs</h3>
                                <p className="text-3xl font-bold text-purple-600">{analyticsData.totalWorkers}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Durée Moyenne</h3>
                                <p className="text-3xl font-bold text-orange-600">{analyticsData.averageDuration}min</p>
                            </div>
                        </div>

                        {/* Charts Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                            {/* Monthly Exchanges Chart */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Échanges par mois</h3>
                                <div className="space-y-3">
                                    {analyticsData.monthlyExchanges.map((item, index) => (
                                        <div key={index} className="flex items-center">
                                            <span className="w-20 text-sm text-gray-600">{item.month}</span>
                                            <div className="flex-1 mx-3 bg-gray-200 rounded-full h-4">
                                                <div
                                                    className="bg-blue-600 h-4 rounded-full"
                                                    style={{ width: `${(item.count / Math.max(...analyticsData.monthlyExchanges.map(i => i.count))) * 100}%` }}
                                                ></div>
                                            </div>
                                            <span className="w-8 text-sm font-semibold">{item.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Exchange Formats */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Formats d'échange</h3>
                                <div className="space-y-3">
                                    {analyticsData.exchangeFormats.map((item, index) => (
                                        <div key={index} className="flex items-center">
                                            <span className="w-32 text-sm text-gray-600 truncate">{item.format}</span>
                                            <div className="flex-1 mx-3 bg-gray-200 rounded-full h-4">
                                                <div
                                                    className="bg-green-600 h-4 rounded-full"
                                                    style={{ width: `${(item.count / Math.max(...analyticsData.exchangeFormats.map(i => i.count))) * 100}%` }}
                                                ></div>
                                            </div>
                                            <span className="w-8 text-sm font-semibold">{item.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Distribution kit RDR */}
                        {analyticsData.rdrDistribution.length > 0 && (
                            <div className="bg-white rounded-lg shadow p-6 mb-8">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">Distribution kit RDR</h3>
                                <p className="text-xs text-gray-400 mb-4">
                                    {analyticsData.rdrDistribution.reduce((s, i) => s + i.count, 0)} distributions sur {analyticsData.totalExchanges} échanges
                                </p>
                                <div className="space-y-3">
                                    {analyticsData.rdrDistribution.map((item, index) => (
                                        <div key={index} className="flex items-center">
                                            <span className="w-48 text-sm text-gray-600 truncate">{item.value}</span>
                                            <div className="flex-1 mx-3 bg-gray-200 rounded-full h-4">
                                                <div
                                                    className="bg-purple-500 h-4 rounded-full"
                                                    style={{ width: `${(item.count / Math.max(...analyticsData.rdrDistribution.map(i => i.count))) * 100}%` }}
                                                />
                                            </div>
                                            <span className="w-8 text-sm font-semibold">{item.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Score Distribution */}
                        <div className="bg-white rounded-lg shadow p-6 mb-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribution des scores moyens</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {analyticsData.scoreDistribution.map((item, index) => (
                                    <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize">{item.category}</h4>
                                        <p className="text-2xl font-bold text-blue-600">{item.avg_score}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </main>
            )}
        </div>
    )
} 